/*
  variant-guard.js  -  Saro Jewelry
  --------------------------------------------------------------
  Purpose: guarantee that the variant the customer SEES selected
  (size / colour / length ...) is the exact variant that is sent
  to /cart/add.

  Why it is needed: the theme updates the hidden `id` field only
  AFTER an async Section-Rendering request finishes. A customer who
  taps a swatch and then taps "Add to cart" before that request
  returns gets the PREVIOUS variant. This file closes that window.

  Design rules:
   * Purely additive. It never removes or rewrites theme behaviour.
   * If it cannot resolve a variant with full confidence it does
     NOTHING and lets the theme behave exactly as before.
   * Every hook is wrapped in try/catch so it can never break a page.
*/
(function () {
  'use strict';

  if (window.__variantGuardInstalled) return;
  window.__variantGuardInstalled = true;

  var SEP = '~|~';

  function norm(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function closest(el, sel) {
    if (!el || !el.closest) return null;
    try { return el.closest(sel); } catch (e) { return null; }
  }

  /* ---------- scope + variant map ---------- */

  function scopeOf(el) {
    return closest(el, 'main-product-form') || closest(el, 'form[action*="/cart/add"]');
  }

  var CACHE = {};          /* handle -> {optionCount, variants:[{id,o}]} */
  var PENDING = {};

  function root() {
    try {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) return Shopify.routes.root;
    } catch (e) {}
    return '/';
  }

  function handleOf(scope) {
    if (!scope) return null;
    var f = scope.querySelector ? scope.querySelector('form[data-handle]') : null;
    if (!f && scope.getAttribute) f = scope.getAttribute('data-handle') ? scope : null;
    var h = f ? f.getAttribute('data-handle') : null;
    if (h) return h;
    try {
      var m = String(location.pathname).match(/\/products\/([^\/?#]+)/);
      if (m) return decodeURIComponent(m[1]);
    } catch (e) {}
    return null;
  }

  function parseNode(node) {
    if (!node) return null;
    if (node.__vgParsed !== undefined) return node.__vgParsed;
    try { node.__vgParsed = JSON.parse(node.textContent); }
    catch (e) { node.__vgParsed = null; }
    return node.__vgParsed;
  }

  function prefetch(handle) {
    if (!handle || CACHE[handle] || PENDING[handle]) return;
    PENDING[handle] = true;
    try {
      var url = root().replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '.js';
      var origFetchRef = window.__vgOrigFetch || window.fetch;
      origFetchRef.call(window, url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.variants) return;
          CACHE[handle] = {
            handle: handle,
            optionCount: (p.options && p.options.length) || 0,
            variants: p.variants.map(function (v) {
              return { id: v.id, o: (v.options || []).slice(0, (p.options && p.options.length) || (v.options || []).length), a: v.available };
            })
          };
          applyAll();
        })
        .catch(function () {});
    } catch (e) {}
  }

  function mapOf(scope) {
    if (!scope) return null;
    /* 1. map rendered inside this scope */
    var node = scope.querySelector ? scope.querySelector('script[data-variant-guard]') : null;
    var data = parseNode(node);
    if (data && data.variants && data.variants.length) return data;

    var handle = handleOf(scope);

    /* 2. map rendered by the layout, matched strictly by product handle */
    if (handle) {
      var nodes = document.querySelectorAll('script[data-variant-guard]');
      for (var i = 0; i < nodes.length; i++) {
        var d = parseNode(nodes[i]);
        if (d && d.handle === handle && d.variants && d.variants.length) return d;
      }
      /* 3. cached product JSON */
      if (CACHE[handle]) return CACHE[handle];
      prefetch(handle);
    }
    return null;
  }

  /* ---------- read what the customer has chosen ---------- */

  function valueOfItem(li) {
    if (!li) return null;
    var r = li.querySelector('input[type="radio"]');
    return li.getAttribute('data-swatch') ||
           li.getAttribute('title') ||
           (r ? r.value : null) ||
           (li.textContent || '').trim();
  }

  function pickerRoot(scope) {
    if (!scope || !scope.querySelector) return scope;
    return scope.querySelector('variant-selector') || scope;
  }

  function ownFormOf(scope) {
    if (!scope) return null;
    if (scope.tagName === 'FORM') return scope;
    return scope.querySelector('form[action*="/cart/add"]');
  }

  function readSelection(scope, override) {
    var nodes = pickerRoot(scope).querySelectorAll('.variant-options, select[name^="options["]');
    if (!nodes.length) return null;
    var values = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i], v = null;
      if (n.tagName === 'SELECT') {
        var opt = n.options[n.selectedIndex];
        v = opt ? (opt.getAttribute('data-option-value') || opt.value) : null;
      } else if (override && n.contains(override)) {
        v = valueOfItem(override);
      } else {
        v = valueOfItem(n.querySelector('.variant-option-item.selected'));
      }
      if (v == null || norm(v) === '') return null;   /* incomplete -> do nothing */
      values.push(v);
    }
    return values;
  }

  /* ---------- resolve ---------- */

  function resolve(scope, override) {
    var data = mapOf(scope);
    if (!data || !data.variants || !data.variants.length) return null;
    var picked = readSelection(scope, override);
    if (!picked) return null;
    if (data.optionCount && picked.length !== data.optionCount) return null;
    var key = picked.map(norm).join(SEP);
    for (var i = 0; i < data.variants.length; i++) {
      var v = data.variants[i];
      if (!v.o || v.o.length !== picked.length) continue;
      if (v.o.map(norm).join(SEP) === key) return v;
    }
    return null;
  }

  function syncRadios(scope, override) {
    var groups = pickerRoot(scope).querySelectorAll('.variant-options');
    for (var g = 0; g < groups.length; g++) {
      var ul = groups[g];
      var chosen = (override && ul.contains(override))
        ? override
        : ul.querySelector('.variant-option-item.selected');
      var items = ul.querySelectorAll('.variant-option-item');
      for (var i = 0; i < items.length; i++) {
        var r = items[i].querySelector('input[type="radio"]');
        if (r) r.checked = (items[i] === chosen);
      }
    }
  }

  function apply(scope, override) {
    if (!scope) return null;
    var v;
    try { v = resolve(scope, override); } catch (e) { return null; }
    if (!v) return null;
    try {
      var form = ownFormOf(scope);
      if (form) {
        var cands = form.querySelectorAll('input[name="id"], select[name="id"], input.product-variant-id');
        for (var i = 0; i < cands.length; i++) {
          /* only fields owned by this form - never a nested/complementary product form */
          if (cands[i].form && cands[i].form !== form) continue;
          if (String(cands[i].value) !== String(v.id)) cands[i].value = String(v.id);
        }
      }
      syncRadios(scope, override);
    } catch (e) {}
    return v;
  }

  function applyAll() {
    try {
      var scopes = document.querySelectorAll('main-product-form, form[action*="/cart/add"][data-handle]');
      for (var i = 0; i < scopes.length; i++) {
        var h = handleOf(scopes[i]);
        if (h && !CACHE[h]) prefetch(h);
        apply(scopes[i]);
      }
    } catch (e) {}
  }

  function reapplySoon() {
    [0, 60, 200, 600, 1200, 2000, 3000].forEach(function (ms) {
      setTimeout(applyAll, ms);
    });
  }

  /* ---------- hooks ---------- */

  /* 1. The instant a swatch / size / length is clicked (capture phase,
        i.e. BEFORE the theme starts its async section request). */
  document.addEventListener('click', function (e) {
    try {
      var item = closest(e.target, '.variant-option-item');
      if (item) {
        var scope = scopeOf(item);
        if (scope) { apply(scope, item); reapplySoon(); }
        return;
      }
      var btn = closest(e.target, '[name="add"], #addToCart, .product-form__submit, .quick-add__submit');
      if (btn) {
        var s = scopeOf(btn) || document.querySelector('main-product-form');
        if (s) apply(s);
      }
    } catch (err) {}
  }, true);

  /* 2. Dropdown variant pickers. */
  document.addEventListener('change', function (e) {
    try {
      var t = e.target;
      if (!t || !t.name || t.name.indexOf('options[') !== 0) return;
      var scope = scopeOf(t);
      if (scope) { apply(scope); reapplySoon(); }
    } catch (err) {}
  }, true);

  /* 3. Native form submit. */
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      if (!f || !f.getAttribute) return;
      var action = f.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      apply(scopeOf(f) || f);
    } catch (err) {}
  }, true);

  /* 4. Re-apply after the theme replaces the product section. */
  try {
    var mo = new MutationObserver(function () {
      clearTimeout(window.__vgMoTimer);
      window.__vgMoTimer = setTimeout(applyAll, 80);
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}

  /* ---------- 5. last line of defence: correct any /cart/add payload ---------- */

  function correctionTable() {
    var table = {}, ambiguous = {};
    try {
      var scopes = document.querySelectorAll('main-product-form, form[action*="/cart/add"][data-handle]');
      for (var i = 0; i < scopes.length; i++) {
        var scope = scopes[i];
        if (!scope.offsetParent && scope.offsetHeight === 0) continue; /* not visible */
        var data = mapOf(scope);
        if (!data) continue;
        var v = resolve(scope);
        if (!v) continue;
        for (var j = 0; j < data.variants.length; j++) {
          var from = String(data.variants[j].id);
          if (table[from] !== undefined && table[from] !== String(v.id)) {
            ambiguous[from] = true;           /* two forms disagree -> leave alone */
          }
          table[from] = String(v.id);
        }
      }
      for (var k in ambiguous) delete table[k];
    } catch (e) { return {}; }
    return table;
  }

  function fixBody(body) {
    var m = correctionTable();
    var has = false; for (var k in m) { has = true; break; }
    if (!has) return body;
    try {
      if (typeof body === 'string') {
        var t = body.trim();
        if (t.charAt(0) === '{' || t.charAt(0) === '[') {
          var o = JSON.parse(t), changed = false;
          if (o && o.id != null && m[String(o.id)]) {
            o.id = Number(m[String(o.id)]) || m[String(o.id)]; changed = true;
          }
          if (o && Object.prototype.toString.call(o.items) === '[object Array]') {
            for (var i = 0; i < o.items.length; i++) {
              var it = o.items[i];
              if (it && it.id != null && m[String(it.id)]) {
                it.id = Number(m[String(it.id)]) || m[String(it.id)]; changed = true;
              }
            }
          }
          return changed ? JSON.stringify(o) : body;
        }
        var p = new URLSearchParams(body);
        var pid = p.get('id');
        if (pid != null && m[String(pid)]) { p.set('id', m[String(pid)]); return p.toString(); }
        return body;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        var uid = body.get('id');
        if (uid != null && m[String(uid)]) body.set('id', m[String(uid)]);
        return body;
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var fid = body.get('id');
        if (fid != null && m[String(fid)]) body.set('id', m[String(fid)]);
        return body;
      }
    } catch (e) {}
    return body;
  }

  function isCartAdd(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  try {
    var origFetch = window.fetch;
    window.__vgOrigFetch = origFetch;
    if (typeof origFetch === 'function') {
      window.fetch = function (input, init) {
        try {
          var url = (typeof input === 'string') ? input : (input && input.url) || '';
          if (isCartAdd(url) && init && init.body) {
            init = Object.assign({}, init, { body: fixBody(init.body) });
          }
        } catch (e) {}
        return origFetch.call(this, input, init);
      };
    }
  } catch (e) {}

  try {
    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype) {
      var origOpen = XHR.prototype.open;
      var origSend = XHR.prototype.send;
      XHR.prototype.open = function (method, url) {
        try { this.__vgCartAdd = isCartAdd(url); } catch (e) {}
        return origOpen.apply(this, arguments);
      };
      XHR.prototype.send = function (body) {
        try { if (this.__vgCartAdd && body) body = fixBody(body); } catch (e) {}
        return origSend.call(this, body);
      };
    }
  } catch (e) {}

  /* ---------- boot ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reapplySoon);
  } else {
    reapplySoon();
  }
})();
